import { ShieldCheck, Anchor, Eye, Gem, Gauge, Handshake } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { useSiteImages } from "@/lib/siteImages";
import { useLang } from "@/lib/i18n";

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1777264935754-aa53096c2d68?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBib2FyZHJvb218ZW58MHx8fHwxNzg4MjI0MTAxfDA&ixlib=rb-4.1.0&q=85";

const VALUE_ICONS = [ShieldCheck, Anchor, Eye, Gem, Gauge, Handshake];

export const About = () => {
  const si = useSiteImages();
  const { t } = useLang();
  return (
  <section id="about" data-testid="about-section" className="py-24 md:py-32">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-7">
          <Reveal>
            <Eyebrow>{t.about.eyebrow}</Eyebrow>
            <h2 className="text-3xl md:text-4xl tracking-tight font-bold text-[#F4F6F8] max-w-lg">
              {t.about.title}
            </h2>
          </Reveal>

          <div className="mt-16 space-y-14">
            {t.about.chapters.map((ch, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div data-testid={`about-chapter-${String(i + 1).padStart(2, "0")}`} className="relative flex gap-6 md:gap-10 items-start">
                  <span className="font-display font-black text-6xl md:text-7xl text-outline leading-none select-none shrink-0 w-24 md:w-32">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="pt-2">
                    <h3 className="text-xl md:text-2xl font-semibold text-gold mb-3">{ch.title}</h3>
                    <p className="text-base text-[#94A3B8] leading-relaxed max-w-xl">{ch.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          <Reveal delay={0.15} className="lg:sticky lg:top-28">
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full border border-gold/40 pointer-events-none" />
              <div className="relative overflow-hidden">
                <img
                  src={si("about", ABOUT_IMG)}
                  alt="Norex corporate boardroom"
                  className="w-full h-[420px] lg:h-[520px] object-cover"
                />
                <div className="absolute inset-0 bg-[#060A14]/25" />
                <div className="absolute bottom-0 inset-x-0 p-6 bg-[#060A14]/70 backdrop-blur-md border-t border-white/10">
                  <p className="text-xs uppercase tracking-[0.25em] text-gold mb-1">{t.about.officeLabel}</p>
                  <p className="text-sm text-[#F4F6F8]">{t.about.officeValue}</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      <div className="mt-24">
        <Reveal>
          <h3 className="text-lg md:text-xl font-medium text-[#F4F6F8] mb-8 flex items-center gap-4">
            <span className="w-8 h-px bg-gold" /> {t.about.valuesTitle}
          </h3>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {t.about.values.map((label, i) => {
            const Icon = VALUE_ICONS[i];
            return (
              <Reveal key={i} delay={i * 0.06}>
                <div
                  data-testid={`value-${i + 1}`}
                  className="group bg-surface border border-[#1E293B] p-6 h-full hover:border-gold/50 hover:-translate-y-1 transition-[border-color,transform] duration-300"
                >
                  <Icon className="w-6 h-6 text-gold mb-4 transition-transform duration-300 group-hover:scale-110" />
                  <p className="text-sm font-semibold text-[#F4F6F8] leading-snug">{label}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </div>
  </section>
  );
};
